import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Alert,
  useTheme,
  Autocomplete,
  Chip,
  Box,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useMutation, useQuery } from "@apollo/client";
import { UPDATE_AUDIT } from "../../../../../apis/settingsApis/audit-apis/auditApis";
import { GET_FRAMEWORK_LIST } from "../../../../../apis/settingsApis/framework-apis/frameworkApis";
import { GET_EXTERNAL_USERS } from "../../../../../apis/organization-apis/people-apis/externalUserApis";
import { GET_STAFF_DETAILS } from "../../../../../apis/organization-apis/people-apis/peopleApis";
import { LoadingButton } from "@mui/lab";
import moment from "moment";
import { AuditStatus, AuditStatusLabel } from "../../../../../utils/enums";
import DatePicker from "../../../../../components/DatePicker/DatePicker";
import { getStartOfDayUTC, getEndOfDayUTC } from "../../../../../utils/dateUtils";
import { firstActiveAuditorRowPerId } from "../../utils/auditDetailsUtils";

const EditAuditDialog = ({ open, onClose, setSnackbarInfo, refetchAudits, audit, navigate, isAuditorView = false }) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    framework_id: "",
    auditors: [],
    audit_type: "",
    audit_status: AuditStatus.ACTIVE,
    audit_start_date: "",
    audit_end_date: "",
  });
  const [errors, setErrors] = useState({});

  const [updateAudit, { loading: updating }] = useMutation(UPDATE_AUDIT);

  const { data: frameworksData } = useQuery(GET_FRAMEWORK_LIST, {
    fetchPolicy: "cache-first",
  });
  const { data: externalUsersData } = useQuery(GET_EXTERNAL_USERS, {
    variables: { page: 1, limit: 100 },
    fetchPolicy: "cache-and-network",
  });
  const { data: employeesData } = useQuery(GET_STAFF_DETAILS, {
    fetchPolicy: "cache-and-network",
  });

  const frameworks = frameworksData?.getFramework || [];

  // External auditor options (is_internal: false)
  const externalAuditorOptions = useMemo(() =>
    firstActiveAuditorRowPerId(externalUsersData?.getExternalUsers?.data || []).map((u) => ({
      id: String(u.id),
      name: u.name || "",
      email: u.email || "",
      is_internal: false,
    })),
    [externalUsersData]
  );

  // Internal employee options (is_internal: true)
  const internalEmployeeOptions = useMemo(() =>
    (employeesData?.getEmployees?.data || [])
      .filter((emp) => emp.employee_data_id)
      .map((emp) => ({
        id: String(emp.employee_data_id),
        name: emp.name || emp.work_email?.split("@")[0] || "",
        email: emp.work_email || "",
        is_internal: true,
      })),
    [employeesData]
  );

  // For external audits: only external; for internal: combined
  const auditorOptions = useMemo(() => {
    if (formData.audit_type === "external") return externalAuditorOptions;
    return [...internalEmployeeOptions, ...externalAuditorOptions];
  }, [formData.audit_type, internalEmployeeOptions, externalAuditorOptions]);

  const isCompleted = audit?.audit_status === AuditStatus.COMPLETED;

  useEffect(() => {
    if (open && audit) {
      // auditors field now comes directly from API
      const selectedAuditors = Array.isArray(audit.auditors) ? audit.auditors : [];

      setFormData({
        framework_id: audit.framework_id?.toString() || "",
        auditors: selectedAuditors,
        audit_type: audit.audit_type || "",
        audit_status: audit.audit_status || AuditStatus.ACTIVE,
        audit_start_date: audit.audit_start_date
          ? moment(audit.audit_start_date).format("YYYY-MM-DD")
          : "",
        audit_end_date: audit.audit_end_date
          ? moment(audit.audit_end_date).format("YYYY-MM-DD")
          : "",
      });
      setErrors({});
    }
  }, [open, audit]);

  // Clear internal auditors if audit_type switches to external
  useEffect(() => {
    if (formData.audit_type === "external" && formData.auditors?.some((a) => a.is_internal)) {
      setFormData((prev) => ({
        ...prev,
        auditors: prev.auditors.filter((a) => !a.is_internal),
      }));
    }
  }, [formData.audit_type]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.framework_id) {
      newErrors.framework_id = "Framework is required";
    }
    if (!formData.audit_type) {
      newErrors.audit_type = "Audit type is required";
    }
    if (!formData.auditors || formData.auditors.length === 0) {
      newErrors.auditors = "At least one auditor is required";
    }
    if (formData.audit_type === "external" && formData.auditors?.some((a) => a.is_internal)) {
      newErrors.auditors = "External audits can only have external auditors assigned";
    }
    if (!formData.audit_start_date) {
      newErrors.audit_start_date = "Start date is required";
    }
    if (!formData.audit_end_date) {
      newErrors.audit_end_date = "End date is required";
    }
    if (formData.audit_start_date && formData.audit_end_date) {
      const startDate = new Date(formData.audit_start_date);
      const endDate = new Date(formData.audit_end_date);
      if (endDate <= startDate) {
        newErrors.audit_end_date = "End date must be after start date";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (isCompleted) {
      setSnackbarInfo({
        message: "Cannot update a completed audit",
        vertical: "top",
        horizontal: "right",
      });
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      const input = [
        {
          id: audit.id,
          framework_id: parseInt(formData.framework_id),
          audit_type: formData.audit_type,
          audit_status: formData.audit_status,
          audit_start_date: getStartOfDayUTC(formData.audit_start_date),
          audit_end_date: getEndOfDayUTC(formData.audit_end_date),
          auditors: formData.auditors.map((a) => ({ id: String(a.id), is_internal: a.is_internal })),
        },
      ];

      const result = await updateAudit({
        variables: { input },
      });

      if (result.data?.updateAudit?.success) {
        const newAuditId = result.data?.updateAudit?.data?.[0]?.id;

        setSnackbarInfo({
          message: "Audit updated successfully!",
          vertical: "top",
          horizontal: "right",
        });

        onClose();
        if (navigate && newAuditId && newAuditId !== audit.id) {
          const basePath = isAuditorView ? "/auditor/audits" : "/admin/audits";
          navigate(`${basePath}/${newAuditId}`, { replace: true });
        } else {
          refetchAudits();
        }
      } else {
        setSnackbarInfo({
          message: result.data?.updateAudit?.message || "Failed to update audit",
          vertical: "top",
          horizontal: "right",
        });
      }
    } catch (error) {
      console.error("Error updating audit:", error);
      setSnackbarInfo({
        message: error.message || "An error occurred while updating the audit",
        vertical: "top",
        horizontal: "right",
      });
    }
  };

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  if (!audit) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          fontSize: "1.25rem",
          color: theme.customColors.text.strong,
          background: theme.customColors.background.cardHeader,
          borderBottom: `1px solid ${theme.customColors.border.main}`,
          pb: 2,
          pt: 2.5,
          px: 3,
          position: "relative",
        }}
      >
        Edit Audit
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            color: theme.customColors.text.secondary,
            "&:hover": {
              backgroundColor: theme.customColors.background.hover,
              color: theme.customColors.text.strong,
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          px: 3,
          py: 3,
          "&.MuiDialogContent-root": {
            paddingTop: 2.5,
          },
        }}
      >
        {isCompleted && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This audit is completed and cannot be edited.
          </Alert>
        )}

        <FormControl fullWidth sx={{ mb: 2 }} error={!!errors.framework_id} size="small">
          <InputLabel>Framework *</InputLabel>
          <Select
            value={formData.framework_id}
            label="Framework *"
            onChange={handleChange("framework_id")}
            disabled={updating || isCompleted}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: theme.customColors.border.strong,
                },
              },
            }}
          >
            {frameworks
              .filter((f) => f.is_active)
              .map((framework) => (
                <MenuItem key={framework.framework_id} value={framework.framework_id}>
                  {framework.framework_name}
                </MenuItem>
              ))}
          </Select>
          {errors.framework_id && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {errors.framework_id}
            </Alert>
          )}
        </FormControl>

        <FormControl fullWidth sx={{ mb: 2 }} error={!!errors.audit_type} size="small">
          <InputLabel>Audit Type *</InputLabel>
          <Select
            value={formData.audit_type}
            label="Audit Type *"
            onChange={handleChange("audit_type")}
            disabled={updating || isCompleted}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: theme.customColors.border.strong,
                },
              },
            }}
          >
            <MenuItem value="internal">Internal</MenuItem>
            <MenuItem value="external">External</MenuItem>
          </Select>
          {errors.audit_type && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {errors.audit_type}
            </Alert>
          )}
        </FormControl>

        <FormControl fullWidth sx={{ mb: 2 }} size="small">
          <InputLabel>Audit Status</InputLabel>
          <Select
            value={formData.audit_status}
            label="Audit Status"
            onChange={handleChange("audit_status")}
            disabled={updating || isCompleted}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: theme.customColors.border.strong,
                },
              },
            }}
          >
            <MenuItem value={AuditStatus.ACTIVE}>{AuditStatusLabel[AuditStatus.ACTIVE]}</MenuItem>
            <MenuItem value={AuditStatus.COMPLETED}>{AuditStatusLabel[AuditStatus.COMPLETED]}</MenuItem>
            <MenuItem value={AuditStatus.ARCHIVED}>{AuditStatusLabel[AuditStatus.ARCHIVED]}</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ mb: 2 }}>
          <Typography
            component="label"
            sx={{
              display: "block",
              fontSize: "0.875rem",
              fontWeight: 600,
              color: theme.customColors?.text?.strong ?? "#374151",
              mb: 0.75,
            }}
          >
            Auditors *
          </Typography>
          {formData.audit_type === "external" && (
            <Typography
              variant="caption"
              sx={{ display: "block", mb: 0.5, color: theme.customColors?.text?.secondary }}
            >
              External audits can only have external auditors assigned.
            </Typography>
          )}
          <Autocomplete
            multiple
            options={auditorOptions}
            value={formData.auditors || []}
            onChange={(_, value) => {
              setFormData((prev) => ({ ...prev, auditors: value }));
              if (errors.auditors) {
                setErrors((prev) => {
                  const newErrors = { ...prev };
                  delete newErrors.auditors;
                  return newErrors;
                });
              }
            }}
            disabled={updating || isCompleted}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            isOptionEqualToValue={(option, value) =>
              String(option.id) === String(value.id) && option.is_internal === value.is_internal
            }
            groupBy={(option) => (option.is_internal ? "Internal Employees" : "External Auditors")}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  width: "100%",
                  py: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, width: "100%" }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: theme.customColors.text.primary }}>
                    {option.name}
                  </Typography>
                  <Chip
                    label={option.is_internal ? "Internal" : "External"}
                    size="small"
                    sx={{
                      height: 16,
                      fontSize: "0.6rem",
                      bgcolor: option.is_internal ? "#EEF2FF" : "#F0FDF4",
                      color: option.is_internal ? "#4F46E5" : "#16A34A",
                      "& .MuiChip-label": { px: 0.5 },
                    }}
                  />
                </Box>
                <Typography variant="caption" sx={{ color: theme.customColors.text.secondary }}>
                  {option.email}
                </Typography>
              </Box>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={`${option.id}-${option.is_internal}`}
                  label={option.name}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderRadius: "8px",
                    border: `1px solid ${option.is_internal ? "#A5B4FC" : theme.customColors?.border?.main ?? "#E8EAED"}`,
                    backgroundColor: option.is_internal ? "#EEF2FF" : (theme.customColors?.background?.main ?? "#FFFFFF"),
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={
                  formData.auditors?.length > 0
                    ? ""
                    : auditorOptions.length === 0
                    ? "No auditors available"
                    : "Search and select auditors"
                }
                variant="outlined"
                size="small"
                error={!!errors.auditors}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                    "&:hover fieldset": {
                      borderColor: theme.customColors.border.strong,
                    },
                  },
                }}
              />
            )}
            noOptionsText="No auditors available"
          />
          {errors.auditors && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {errors.auditors}
            </Alert>
          )}
        </Box>

        <DatePicker
          fullWidth
          label="Start Date"
          required
          value={formData.audit_start_date}
          onChange={(date) => handleChange("audit_start_date")({ target: { value: date } })}
          disabled={updating || isCompleted}
          error={!!errors.audit_start_date}
          helperText={errors.audit_start_date}
          size="small"
          sx={{ mb: 2 }}
        />

        <DatePicker
          fullWidth
          label="End Date"
          required
          value={formData.audit_end_date}
          onChange={(date) => handleChange("audit_end_date")({ target: { value: date } })}
          disabled={updating || isCompleted}
          error={!!errors.audit_end_date}
          helperText={errors.audit_end_date}
          size="small"
        />
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          px: 3,
          pb: 2.5,
          pt: 2,
          background: theme.customColors.background.cardHeader,
          borderTop: `1px solid ${theme.customColors.border.main}`,
          position: 'sticky',
          bottom: 0,
          zIndex: 2,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          sx={{
            fontWeight: 500,
            fontSize: "0.875rem",
            px: 2.5,
            py: 0.875,
            borderRadius: 1,
            borderColor: theme.customColors.border.main,
            color: theme.customColors.text.strong,
            "&:hover": {
              borderColor: theme.customColors.border.strong,
              backgroundColor: theme.customColors.background.hover,
              color: theme.customColors.text.strong,
            },
          }}
        >
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSubmit}
          variant="contained"
          loading={updating}
          disabled={isCompleted || !formData.auditors?.length}
          size="small"
          sx={{
            fontWeight: 500,
            fontSize: "0.875rem",
            px: 2.5,
            py: 0.875,
            borderRadius: 1,
            bgcolor: theme.customColors.button.hover,
            color: theme.customColors.button.text,
            "&:hover": {
              bgcolor: theme.customColors.button.primary,
            },
            "&:disabled": {
              bgcolor: theme.customColors.background.light,
              color: theme.customColors.text.disabled,
            },
          }}
        >
          Update Audit
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditAuditDialog;
